import { useMemo } from "react";
import { Alert, Drawer, Empty, List, Spin, Typography, Card, Button, Divider } from "antd";
import { RiExternalLinkLine, RiFileTextLine } from "react-icons/ri";
import { ApiService } from "api/api.service";

const { Text } = Typography;

type LoanFilesDrawerProps = {
  appkey: string;
  open: boolean;
  onClose: () => void;
};

export const LoanFilesDrawer = ({ appkey, open, onClose }: LoanFilesDrawerProps) => {
  const { data, isLoading, isError } = ApiService.upstream.useGetLoanFilesPreview(appkey);

  const groupedFiles = useMemo(() => {
    const groups = new Map<
      string,
      {
        creditor_name?: string;
        files: NonNullable<typeof data>["result"];
      }
    >();

    (data?.result ?? []).forEach((file) => {
      if (!groups.has(file.loan_id)) {
        groups.set(file.loan_id, {
          creditor_name: file.creditor_name,
          files: [],
        });
      }

      groups.get(file.loan_id)!.files.push(file);
    });

    return [...groups.entries()];
  }, [data]);

  return (
    <Drawer title="Loan Documents" open={open} onClose={onClose} width={520} destroyOnClose>
      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spin size="large" />
          </div>
        ) : isError ? (
          <Alert type="error" message="Failed to load loan documents." showIcon />
        ) : !data?.result ? (
          <Empty description="Loan details not found." />
        ) : groupedFiles.length === 0 ? (
          <Empty description="No loan documents available." />
        ) : (
          <div className="flex flex-col gap-5">
            {groupedFiles.map(([loanId, group], index) => (
              <Card
                key={loanId}
                size="small"
                className="shadow-sm"
                title={
                  <div className="flex items-center justify-between">
                    <Text strong>{group.creditor_name?.trim() || loanId}</Text>
                    <Text type="secondary">
                      {group.files.length} file
                      {group.files.length > 1 ? "s" : ""}
                    </Text>
                  </div>
                }
              >
                <List
                  split={false}
                  dataSource={group.files}
                  renderItem={(file, fileIndex) => (
                    <>
                      <List.Item className="!px-0 !py-3">
                        <div className="flex w-full items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <RiFileTextLine size={20} className="text-gray-500 shrink-0" />

                            <Text ellipsis={{ tooltip: file.name }} className="!mb-0">
                              {file.name}
                            </Text>
                          </div>

                          <Button
                            type="text"
                            icon={<RiExternalLinkLine size={18} />}
                            href={file.preview_url}
                            target="_blank"
                          >
                            Open
                          </Button>
                        </div>
                      </List.Item>

                      {fileIndex !== group.files.length - 1 && <Divider className="!my-0" />}
                    </>
                  )}
                />
              </Card>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
};
